import React, { useState } from 'react';
import { Typography, Box, Divider, Stack } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import RatingReadOnly from '../common/RatingReadOnly';
import ReviewerProfile from '../common/ReviewerProfile';
import AlbumCover from '../common/AlbumCover';
import { Link } from 'react-router-dom';
import theme from '../../theme';
import ArtistLink from '../common/ArtistLink';
import AlbumTitleLink from '../common/AlbumTitleLink';
import ReviewContentLink from '../common/ReviewContentLink';
import defaultAlbumThumbnail from '../../assets/default_album_thumbnail.svg';
import admarkIcon from '../../assets/admark.png';
import { trackHomeContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, REFERRER_TYPE } from '../../constants/gaEnums';
import { useUserStore } from '../../stores/userStore';

const ReviewCard = ({
  review,
  variant = 'vertical',
  referrerType,
  referrerSection,
  homeSection,
  showAdMark = false,
  coverActionButton,
}) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const albumUrl = review.albumSlug
    ? `/reviews/${review.albumSlug}`
    : `/reviews/local/${review.albumId}`;
  const userReviewUrl = `${albumUrl}/user-reviews/${review.reviewId}`;
  const [imageError, setImageError] = useState(false);
  const navigateState = homeSection
    ? { referrerType: REFERRER_TYPE.HOME }
    : referrerType || referrerSection
      ? { referrerType, referrerSection }
      : undefined;

  const handleError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  const handleDetailClick = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      if (homeSection) {
        trackHomeContentClick(
          {
            section: homeSection,
            contentType: CONTENT_TYPE.ALBUM,
            contentId: review.albumId,
            contentName: review.albumTitle,
          },
          user,
        );
      }
      navigateWithScrollMemory(albumUrl, navigateState ? { state: navigateState } : undefined);
    }
  };

  const handleLinkToUser = (userNickname) => {
    navigateWithScrollMemory(`/users/${encodeURIComponent(userNickname)}`);
  };

  const getReviewPreview = (content, maxLength = 120) => {
    const source = content || '';
    if (source.length <= maxLength) return source;
    return `${source.slice(0, maxLength).trimEnd()}...더보기`;
  };

  // horizontalMobile: xs에서만 가로형, md 이상에서는 세로형
  if (variant === 'horizontalMobile') {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        {/* xs: 가로형 */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
            {/* 좌측 커버 */}
            <Box
              component={Link}
              to={albumUrl}
              onClick={handleDetailClick}
              sx={{
                display: 'block',
                width: '34%',
                maxWidth: 136,
                minWidth: 112,
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <AlbumCover
                imageUrl={
                  imageError || !review.albumImageUrl ? defaultAlbumThumbnail : review.albumImageUrl
                }
                altText={review.albumTitle}
                isStarred={review.isStarred}
                onError={handleError}
                sx={{
                  aspectRatio: '1 / 1',
                  width: '100%',
                  cursor: 'pointer',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {showAdMark && review.isAdMarked && (
                <Box
                  component="img"
                  src={admarkIcon}
                  alt="광고"
                  sx={{
                    position: 'absolute',
                    top: '5%',
                    right: '5%',
                    width: 18,
                    height: 18,
                  }}
                />
              )}
              {coverActionButton && (
                <Box
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  sx={{
                    position: 'absolute',
                    top: { xs: 8, md: 10 },
                    right: { xs: 8, md: 10 },
                    zIndex: 3,
                  }}
                >
                  {coverActionButton}
                </Box>
              )}
            </Box>

            {/* 우측 정보 */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <AlbumTitleLink
                title={review.albumTitle}
                to={albumUrl}
                state={navigateState}
                contentRating={review.albumContentRating}
                sx={{
                  fontSize: { xs: 14, sm: 15 },
                  lineHeight: 1.24,
                  mb: 1,
                  fontWeight: 700,
                }}
                lineClamp={1}
                iconSx={{
                  width: { xs: '10px', md: '11px', lg: '12px' },
                  height: { xs: '10px', md: '11px', lg: '12px' },
                  verticalAlign: '-0.02em',
                }}
              />
              <ArtistLink
                artistName={review.artist.name}
                artistSource={review.artist.artistSource}
                artistSourceId={review.artist.sourceId}
                artistSlug={review.artist.slug}
                artistDbId={review.artist.id}
                sx={{
                  display: 'block',
                  fontSize: { xs: 13, sm: 14 },
                  lineHeight: 1.25,
                  color: theme.palette.black[4],
                  mb: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              />
              <Typography
                fontSize={{ xs: 13, sm: 14 }}
                lineHeight={1.25}
                color={theme.palette.black[4]}
                sx={{
                  mb: 1,
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {review.albumGenres?.slice(0, 3).join(', ')}
              </Typography>
              <Box
                component={Link}
                to={`/users/${encodeURIComponent(review.userNickname)}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkToUser(review.userNickname);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  maxWidth: '100%',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <ReviewerProfile
                  imageUrl={review.userProfileImageUrl}
                  name={review.userNickname}
                  rating={review.rating}
                  grade={review.userGrade}
                />
              </Box>
            </Box>
          </Box>
          <ReviewContentLink
            content={getReviewPreview(review.content)}
            to={userReviewUrl}
            state={navigateState}
            lineClamp={3}
            sx={{
              fontSize: { xs: 13, sm: 14 },
              lineHeight: 1.5,
              mt: 1.75,
              color: theme.palette.black[6],
            }}
          />
          {review.createdAt && (
            <Typography
              color={theme.palette.black[1]}
              sx={{
                fontSize: { xs: 12, sm: 12 },
                lineHeight: 1.25,
                mt: 0.5,
              }}
            >
              {review.createdAt}
            </Typography>
          )}
        </Box>
        <Divider sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }} />

        {/* md 이상: 세로형 */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <VerticalLayout
            review={review}
            albumUrl={albumUrl}
            userReviewUrl={userReviewUrl}
            navigateState={navigateState}
            imageError={imageError}
            handleError={handleError}
            handleDetailClick={handleDetailClick}
            handleLinkToUser={handleLinkToUser}
            showAdMark={showAdMark}
            coverActionButton={coverActionButton}
          />
        </Box>
      </Box>
    );
  }

  // 기본: 세로형
  return (
    <VerticalLayout
      review={review}
      albumUrl={albumUrl}
      userReviewUrl={userReviewUrl}
      navigateState={navigateState}
      imageError={imageError}
      handleError={handleError}
      handleDetailClick={handleDetailClick}
      handleLinkToUser={handleLinkToUser}
      showAdMark={showAdMark}
      coverActionButton={coverActionButton}
    />
  );
};

// 세로형 레이아웃 컴포넌트
const VerticalLayout = ({
  review,
  albumUrl,
  userReviewUrl,
  navigateState,
  imageError,
  showAdMark,
  handleError,
  handleDetailClick,
  handleLinkToUser,
  coverActionButton,
}) => (
  <Box sx={{ width: '100%', minWidth: 0 }}>
    {/* 앨범 커버 */}
    <Box
      component={Link}
      to={albumUrl}
      onClick={handleDetailClick}
      sx={{ display: 'block', mb: { xs: 1, md: 1.25, lg: 1.5 }, position: 'relative' }}
    >
      <AlbumCover
        imageUrl={
          imageError || !review.albumImageUrl ? defaultAlbumThumbnail : review.albumImageUrl
        }
        altText={review.albumTitle}
        isStarred={review.isStarred}
        onError={handleError}
        sx={{
          aspectRatio: '1 / 1',
          width: '100%',
          cursor: 'pointer',
          objectFit: 'cover',
          display: 'block',
        }}
      />
      {showAdMark && review.isAdMarked && (
        <Box
          component="img"
          src={admarkIcon}
          alt="광고"
          sx={{
            position: 'absolute',
            top: '5%',
            right: '5%',
            width: { xs: 18, md: 20 },
            height: { xs: 18, md: 20 },
          }}
        />
      )}
      {coverActionButton && (
        <Box
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          sx={{
            position: 'absolute',
            top: { xs: 8, md: 10 },
            right: { xs: 8, md: 10 },
            zIndex: 3,
          }}
        >
          {coverActionButton}
        </Box>
      )}
    </Box>

    {/* 앨범 정보 */}
    <AlbumTitleLink
      title={review.albumTitle}
      to={albumUrl}
      state={navigateState}
      contentRating={review.albumContentRating}
      sx={{
        fontSize: { xs: 14, lg: 16 },
        lineHeight: 1.25,
        minHeight: { xs: 17.5, lg: 20 },
        mb: 0.75,
        lineClamp: 1,
      }}
      lineClamp={1}
      iconSx={{
        width: { xs: '10px', md: '11px', lg: '12px' },
        height: { xs: '10px', md: '11px', lg: '12px' },
        verticalAlign: '-0.02em',
      }}
    />
    <ArtistLink
      artistName={review.artist.name}
      artistSource={review.artist.artistSource}
      artistSourceId={review.artist.sourceId}
      artistSlug={review.artist.slug}
      artistDbId={review.artist.id}
      sx={{
        fontSize: { xs: 13, lg: 14 },
        color: theme.palette.black[4],
        lineHeight: 1.25,
        minHeight: { xs: 16.25, lg: 17.5 },
        mb: 0.75,
      }}
    />
    <Typography
      fontSize={{ xs: 13, lg: 14 }}
      color={theme.palette.black[4]}
      lineHeight={1.25}
      mb={{ xs: 1.25, lg: 1.5 }}
      sx={{
        display: { xs: 'none', md: 'block' },
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minHeight: { md: 16.25, lg: 17.5 },
      }}
    >
      {review.albumGenres?.slice(0, 3).join(', ')}
    </Typography>

    {/* 평점 + 날짜: xs/md (평점 → 날짜, column) */}
    <Stack
      direction="column"
      gap={{ xs: '12px', md: '8px' }}
      mb={2}
      sx={{ display: { xs: 'flex', lg: 'none' } }}
    >
      <RatingReadOnly value={review.albumAvgRating} variant="fullResponsive" />
      <Typography
        fontSize={{ xs: 12, md: 13 }}
        color={{ xs: theme.palette.black[2], md: theme.palette.black[4] }}
      >
        {review.albumReleaseDate}
      </Typography>
    </Stack>

    {/* 날짜 + 평점: lg (날짜 | 평점, row) */}
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      mb={2}
      sx={{ display: { xs: 'none', lg: 'flex' } }}
    >
      <Typography fontSize={14} color={theme.palette.black[4]}>
        {review.albumReleaseDate}
      </Typography>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          height: '8px',
          alignSelf: 'center',
          backgroundColor: theme.palette.grey.line,
        }}
      />
      <RatingReadOnly value={review.albumAvgRating} variant="fullResponsive" />
    </Stack>

    {/* 유저 프로필 + 유저 별점 */}
    <Box
      component={Link}
      to={`/users/${encodeURIComponent(review.userNickname)}`}
      onClick={(e) => {
        e.preventDefault();
        handleLinkToUser(review.userNickname);
      }}
      sx={{
        display: 'inline-flex',
        minWidth: 0,
        maxWidth: '100%',
        textDecoration: 'none',
        color: 'inherit',
        mb: { xs: 1.5, lg: 2 },
      }}
    >
      <ReviewerProfile
        imageUrl={review.userProfileImageUrl}
        name={review.userNickname}
        rating={review.rating}
        grade={review.userGrade}
      />
    </Box>

    {/* 리뷰 텍스트 */}
    <ReviewContentLink
      content={review.content}
      to={userReviewUrl}
      state={navigateState}
      lineClamp={3}
      sx={{ fontSize: { xs: 13, lg: 14 } }}
    />

    <Typography
      fontSize={{ xs: 12, md: 13, lg: 14 }}
      color={{ xs: theme.palette.black[2], md: theme.palette.black[4] }}
      sx={{ mt: { xs: 1.5, lg: 2 } }}
    >
      {review.createdAt}
    </Typography>
  </Box>
);

export default ReviewCard;
